import { csvCell, csvWords, pageWords, type Page } from '@fieldia/core';
import type { DesignerWords } from './designer-words';
import { en } from './locales/en';
import { languagesOf, languageTag, pageLanguage } from './translations';

/**
 * The page's words as CSV, for a translator's spreadsheet, and a CSV read
 * back as translations. The first row names the languages by tag, the first
 * column holds the page's words as written, and each other column a
 * language's translations. What a spreadsheet copies — split by tabs — reads
 * as well as CSV split by commas or by semicolons.
 */

/** The split a CSV uses, by its first row: the commas, tabs or semicolons outside quotes, whichever there are most of. */
function splitOf(text: string): string {
  const counts: Record<string, number> = { ',': 0, '\t': 0, ';': 0 };
  let quoted = false;
  for (const char of text) {
    if (char === '"') quoted = !quoted;
    else if (!quoted && (char === '\n' || char === '\r')) break;
    else if (!quoted && char in counts) counts[char]++;
  }
  return Object.keys(counts).reduce((best, split) => (counts[split] > counts[best] ? split : best), ',');
}

/**
 * Rows of fields, as RFC 4180 has them: fields in quotes may hold the split, line breaks and doubled quotes. Blank lines
 * are skipped. Each field as it was written: a formula's start a file kept as words (`toCsv`, `csvCell`) without its '.
 */
export function parseCsv(input: string): string[][] {
  const text = input.replace(/^\uFEFF/, '');
  const split = splitOf(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let at = 0;
  const endRow = () => {
    row.push(csvWords(field));
    if (row.length > 1 || row[0] !== '') rows.push(row);
    row = [];
    field = '';
  };
  while (at < text.length) {
    const char = text[at];
    if (char === '"' && field === '') {
      // A quoted field: to the next quote not doubled, or to the end.
      let end = at + 1;
      for (;;) {
        const quote = text.indexOf('"', end);
        if (quote === -1) {
          field += text.slice(end);
          end = text.length;
          break;
        }
        field += text.slice(end, quote);
        if (text[quote + 1] === '"') {
          field += '"';
          end = quote + 2;
        } else {
          end = quote + 1;
          break;
        }
      }
      at = end;
    } else if (char === split) {
      row.push(csvWords(field));
      field = '';
      at++;
    } else if (char === '\n' || char === '\r') {
      endRow();
      // A Windows line end leaves a blank line between its two halves, and blank lines are skipped.
      at++;
    } else {
      field += char;
      at++;
    }
  }
  if (field !== '' || row.length) endRow();
  return rows;
}

/**
 * Rows as CSV, each field as `csvCell` writes one: words a spreadsheet would run as a formula ("=…", "+…", "-…", "@…")
 * kept as words with a `'` before them, read back without it; a field with a comma, quote, tab, semicolon, line break
 * or space at an end in quotes, its quotes doubled.
 */
export function toCsv(rows: string[][]): string {
  return rows.map((row) => row.map((text) => csvCell(text, ',;\t')).join(',') + '\r\n').join('');
}

/** The page's words and their translations as CSV: the page's language, then each language it keeps. */
export function translationsCsv(page: Page): string {
  const languages = languagesOf(page);
  return toCsv([[pageLanguage(page), ...languages], ...pageWords(page).map((word) => [word, ...languages.map((tag) => page.translations?.[tag]?.[word] ?? '')])]);
}

export type ReadCsv = { words: Record<string, Record<string, string>>; notOnPage: number } | { problem: string };

/**
 * A CSV as translations to fill: each column after the first by the language
 * its first row names, by tag or name; the page's own language and unnamed
 * columns skipped. Words the page no longer has are left out, and counted.
 */
export function readTranslationsCsv(text: string, page: Page, words: DesignerWords = en): ReadCsv {
  const w = words.translations;
  const [head, ...rows] = parseCsv(text);
  if (!head) return { problem: w.nothingToRead };
  const columns: [number, string][] = [];
  for (const [at, name] of head.entries()) {
    if (at === 0 || !name.trim()) continue;
    const tag = languageTag(name, words);
    if (!tag) return { problem: w.notALanguage(name.trim()) };
    if (tag !== pageLanguage(page)) columns.push([at, tag]);
  }
  if (!columns.length) return { problem: w.noLanguageColumn };
  const shown = new Set(pageWords(page));
  const found: Record<string, Record<string, string>> = {};
  let notOnPage = 0;
  for (const row of rows) {
    const source = row[0] ?? '';
    const given = columns.filter(([at]) => row[at]?.trim());
    if (!given.length) continue;
    if (!shown.has(source)) {
      notOnPage++;
      continue;
    }
    for (const [at, tag] of given) (found[tag] ??= {})[source] = row[at];
  }
  return { words: found, notOnPage };
}
